/**
 * cloudSync — thin wrapper around the admin-relevant /api/auth + /api/admin
 * endpoints. Mirrors frontend/src/lib/cloudSync.js but trimmed to what the
 * standalone admin panel actually calls (no deck CRUD).
 * All requests use `credentials: 'include'` so httponly cookies flow.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const request = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    const err = new Error((data && (data.detail || data.message)) || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
};

// ---- Auth ----
export const apiLogin = (email, password) =>
  request("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });

export const apiMe = () => request("/api/auth/me", { method: "GET" });

export const apiLogout = () => request("/api/auth/logout", { method: "POST" });

// ---- Game config (admin-editable format data, e.g. GENESYS points) ----
export const apiAdminGetGenesysConfig = () =>
  request("/api/admin/config/genesys", { method: "GET" });

export const apiAdminPutGenesysConfig = (config) =>
  request("/api/admin/config/genesys", { method: "PUT", body: JSON.stringify(config) });

// ---- Tag review queue (Phase 6) ----
export const apiListTagReviewQueue = (status) =>
  request(`/api/tag-review${status ? `?status=${status}` : ""}`, { method: "GET" });

export const apiResolveTagReviewItem = (id, status, note) =>
  request(`/api/tag-review/${id}`, { method: "PATCH", body: JSON.stringify({ status, note }) });

// ---- Audit log (Phase 1) ----
export const apiListAuditLog = (section, limit = 100) =>
  request(`/api/admin/audit-log?limit=${limit}${section ? `&section=${encodeURIComponent(section)}` : ""}`, { method: "GET" });

// ---- Error log (Phase 1) ----
export const apiListErrors = (status, source) => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (source) params.set("source", source);
  const qs = params.toString();
  return request(`/api/admin/errors${qs ? `?${qs}` : ""}`, { method: "GET" });
};

export const apiUpdateErrorStatus = (id, status) =>
  request(`/api/admin/errors/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });

// ---- Analytics (dashboard KPIs) ----
export const apiAdminAnalyticsSummary = (days = 30) =>
  request(`/api/analytics/summary?days=${days}`, { method: "GET" });

// ---- Card Tag Rules (deck context admin override layer) ----
export const apiListTagRules = () => request("/api/tag-rules", { method: "GET" });

export const apiCreateTagRule = (rule) =>
  request("/api/tag-rules", { method: "POST", body: JSON.stringify(rule) });

export const apiUpdateTagRule = (id, rule) =>
  request(`/api/tag-rules/${id}`, { method: "PUT", body: JSON.stringify(rule) });

export const apiDeleteTagRule = (id) =>
  request(`/api/tag-rules/${id}`, { method: "DELETE" });

export const apiTestTagRule = (rule, sampleDeck) =>
  request("/api/tag-rules/test", { method: "POST", body: JSON.stringify({ rule, sampleDeck }) });

// ---- Banlists (YGO Advance + Edison) ----
// fmt: "adv" | "edi"
export const apiGetBanlist = (fmt) =>
  request(`/api/admin/banlist/${fmt}`, { method: "GET" });

export const apiPutBanlist = (fmt, data) =>
  request(`/api/admin/banlist/${fmt}`, { method: "PUT", body: JSON.stringify(data) });
